import React, { createContext, useContext, useMemo } from 'react'
import PropTypes from 'prop-types'

const TableContext = createContext({
  hover: true,
  bordered: false,
  striped: false,
  responsive: true
})

export const useTableContext = () => useContext(TableContext)

/**
 * Reusable Table component with Bootstrap styling
 *
 * @param {Object} props - Component props
 * @param {boolean} props.hover - Enable hover effect on rows
 * @param {boolean} props.bordered - Show table borders
 * @param {boolean} props.striped - Stripe odd/even rows
 * @param {boolean} props.responsive - Wrap in responsive container
 * @param {string} props.className - Additional CSS classes
 */
const Table = ({
  hover = true,
  bordered = false,
  striped = false,
  responsive = true,
  className = '',
  children,
  ...props
}) => {
  const contextValue = useMemo(
    () => ({ hover, bordered, striped, responsive }), [hover, bordered, striped, responsive]
  )

  const tableClasses = [
    'table',
    hover && 'table-hover',
    bordered && 'table-rounded',
    striped && 'table-striped',
    className
  ].filter(Boolean).join(' ')

  const table = (
    <TableContext.Provider value={contextValue}>
      <table className={tableClasses} {...props}>
        {children}
      </table>
    </TableContext.Provider>
  )

  if (responsive)
    return <div className="table-responsive">{table}</div>

  return table
}

Table.propTypes = {
  bordered: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  hover: PropTypes.bool,
  responsive: PropTypes.bool,
  striped: PropTypes.bool
}

Table.defaultProps = {
  bordered: false,
  className: '',
  hover: true,
  responsive: true,
  striped: false
}

export default Table
